import * as Sentry from "@sentry/browser";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getBootstrapped } from "core-integration/src/store/bootstrap/reducers";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import { getServerError } from "core-integration/src/store/global/reducers";
import { IServerError } from "core-integration/src/store/global/types";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Box } from "rebass/styled-components";
import i18n from "../i18n";
import styled, { css, ThemeProvider } from "styled-components";
import "../fonts.css";
import GlobalStyle from "../GlobalStyle";
import theme from "../theme";
import { getSite, SITE_IDS } from "../utils/sites";
import { AuthContextProvider } from "./auth/AuthContext";
import BadgeBanner from "./BadgeHeader";
import ElementDialog from "./ElementDialog";
import GameHeader from "./GameHeader";
import { Main, Wrapper } from "./Layout";
import LeaderboardAd from "./LeaderboardAd";
import AutoJoinDialog from "./leagues/AutoJoinDialog";
import Routes from "./Routes";
import ServerError from "./ServerError";
const TV2Header = React.lazy(() => import("./sites/TV2Header"));

interface IPropsHeaderWrapper {
  site: string;
}

const headerStyles = {
  euro: css`
    padding-top: 53px;

    @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
      padding-top: 70px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
      padding-top: 163px;
    }
  `,
};

const StyledHeaderWrapper = styled.div<IPropsHeaderWrapper>`
  ${({ site }) => site === "euro" && headerStyles.euro};
`;

interface IProps {
  bootstrapped: boolean;
  serverError: IServerError | null;
}

export const Loading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>{t("loading", "Loading")} ...</Box>
      </Main>
    </Wrapper>
  );
};

const App: React.FC<IProps> = ({ bootstrapped, serverError }) => {
  const site = getSite();
  const siteHeaders: Record<SITE_IDS, React.ReactNode> = {
    main_nb: null,
    main_en: null,
    tv2: <TV2Header />,
  };

  return (
    <React.StrictMode>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        {/* For the translations / site headers */}
        <Suspense fallback={<div />}>
          <title>{i18n.t("app.gameName", "Eliteserien Fantasy")}</title>
          <meta
            name="description"
            content={i18n.t<string>(
              "app.description1n",
              "Eliteserien Fantasy is about competing with your friends, family, colleagues, experts—and all of Norway—to assemble the best team of Eliteserien players before the season starts.",
            )}
          />
          <AuthContextProvider>
            <div className="ism">
              <StyledHeaderWrapper site={site}>
                {siteHeaders[site]}
              </StyledHeaderWrapper>
              <BadgeBanner />
              <GameHeader />
              {serverError ? (
                <ServerError error={serverError} />
              ) : bootstrapped ? (
                <>
                  <LeaderboardAd />
                  <Routes />
                </>
              ) : (
                <Loading />
              )}
              <AutoJoinDialog />
              <ElementDialog />
            </div>
          </AuthContextProvider>
        </Suspense>
      </ThemeProvider>
    </React.StrictMode>
  );
};

interface IContainerPropsFromState {
  bootstrapped: boolean;
  serverError: IServerError | null;
}

interface IState {
  eventId: string | null;
  error: Error | null;
}

interface IContainerPropsFromDispatch {
  bootstrapApp: () => Promise<void>;
}
type ContainerProps = IContainerPropsFromState & IContainerPropsFromDispatch;
class AppContainer extends React.Component<ContainerProps, IState> {
  public state: IState = { error: null, eventId: null };

  public componentDidMount() {
    this.props.bootstrapApp();
  }

  public componentDidCatch(error: Error | null, errorInfo: object) {
    this.setState({ error });
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
      Sentry.withScope((scope) => {
        // Sentry's setExtras expects a record of string keys to any values.
        // errorInfo is an object, but may not have string keys, so we cast it.
        scope.setExtras(errorInfo as Record<string, any>);
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    }
  }

  public render() {
    // JS Error
    if (this.state.error) {
      // Can't reliably use any of our components, eg Button. This should
      //probably be in Norwegian can't use react to do it though ...
      return (
        <>
          <h2>Error</h2>
          <p>We're sorry - something's gone wrong.</p>
          {import.meta.env.VITE_SENTRY_ENVIRONMENT && (
            <>
              <p>
                Our team has been notified, but click here to fill out a report.
              </p>
              <button
                onClick={() =>
                  Sentry.showReportDialog({
                    eventId: this.state.eventId as string,
                  })
                }
              >
                Report feedback
              </button>
            </>
          )}
        </>
      );
    }
    // All OK - Render App
    return (
      <App
        bootstrapped={this.props.bootstrapped}
        serverError={this.props.serverError}
      />
    );
  }
}

const mapStateToProps = (state: RootState): IContainerPropsFromState => ({
  bootstrapped: getBootstrapped(state),
  serverError: getServerError(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
): IContainerPropsFromDispatch => ({
  bootstrapApp: () => dispatch(bootstrap()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
